import React, { useState, useMemo } from "react";
import axios from "axios";
import DeleteSweepOutlinedIcon from "@mui/icons-material/DeleteSweepOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CircularProgress from "@mui/material/CircularProgress";
import { Permission, InputShareFieldProps, ResourceType } from "./entities";
import { createLogger } from "debug/DebugingLogger";
import { UserModelRole } from "target_wasm";
import { Button } from "components/common/Button";
import { TooltipButton } from "components/common/TooltipButton";
import {
  EmailChipInput,
  EmailChip,
  EmailChipInputProps,
  isRefused,
} from "./EmailChipInput";
import { useShareScope } from "./useShareScope";
import {
  MODEL_ROLES,
  RoleOption,
  RoleSelect,
  TABLE_ACCESS_OPTIONS,
} from "./PermissionControl";
import { plural } from "components/common/setUtils";

const log = createLogger("ModelServices");

const CAPACITY_FALLBACK =
  "We've hit our account limit, so we couldn't invite everyone yet.";
const SHARE_FALLBACK = "Failed to share. Please try again.";

/// A refusal carries the door's own wording: only the server knows whether naming
/// the refused addresses would reveal who has a MyCorr account.
const shareErrorMessage = (e: unknown): string => {
  if (!axios.isAxiosError(e)) return SHARE_FALLBACK;
  const data = e.response?.data;
  if (data?.status === "capacity_reached")
    return data.message ?? CAPACITY_FALLBACK;
  if (typeof data?.reason !== "string") return SHARE_FALLBACK;
  const refused: string[] = Array.isArray(data.refused_emails)
    ? data.refused_emails
    : [];
  return refused.length ? `${data.reason} ${refused.join(", ")}` : data.reason;
};

/// Addresses, a role and Share on one row: every place people are given
/// access. Share turns on only with nothing typed or being checked and a valid
/// chip, and confirms on the button, as wide as "✓ Shared", so nothing moves.
export const ShareComposer: React.FC<{
  chips: EmailChip[];
  onChipsChange: (chips: EmailChip[]) => void;
  chipInput: Omit<
    EmailChipInputProps,
    "chips" | "onChipsChange" | "error" | "onErrorChange" | "onInputChange"
  >;
  roles?: RoleOption[];
  role?: string;
  onRole?: (role: string) => void;
  onShare: () => void;
  sharing: boolean;
  shared: boolean;
  disabled?: boolean;
  error: string | null;
  onErrorChange: (error: string | null) => void;
  /// Announced once a share lands.
  sharedMessage?: string;
  onEdit?: () => void;
  /// The button's words before and after: "Share" / "Shared" unless given.
  verb?: { act: string; done: string };
}> = (props) => {
  const verb = props.verb ?? { act: "Share", done: "Shared" };
  const [typed, setTyped] = useState("");
  const refused = props.chips.filter(isRefused);
  const ready =
    !typed.trim() &&
    !props.chips.some((chip) => chip.status === "validating") &&
    props.chips.some((chip) => chip.status === "valid");
  return (
    <div className="flex flex-col gap-1 overflow-visible">
      <div className="flex flex-wrap items-start gap-2">
        <div className="basis-64 grow min-w-0">
          <EmailChipInput
            {...props.chipInput}
            chips={props.chips}
            maxRows={3}
            error={props.error}
            onErrorChange={props.onErrorChange}
            onChipsChange={(next) => {
              props.onEdit?.();
              props.onChipsChange(next);
            }}
            onInputChange={(value) => {
              setTyped(value);
              if (value) props.onEdit?.();
            }}
            currentUserEmail={localStorage.getItem("email") ?? undefined}
          />
        </div>
        {props.roles && props.role && props.onRole && (
          <RoleSelect
            roles={props.roles}
            current={props.role}
            onPick={props.onRole}
            label="Role for new people"
          />
        )}
        {/* Blue from the click to the tick: `loading` would flash the disabled grey. */}
        <Button
          onClick={props.onShare}
          disabled={
            props.disabled || (!ready && !props.sharing && !props.shared)
          }
          aria-busy={props.sharing || undefined}
          variant="primary"
          size="sm"
          icon={
            props.sharing ? (
              <CircularProgress size={12} sx={{ color: "currentColor" }} />
            ) : props.shared ? (
              <CheckIcon className="icon-xs" />
            ) : undefined
          }
          className={`h-8! min-w-22 px-(--control-padding-x-md)! shrink-0 ${props.sharing || props.shared ? "pointer-events-none" : ""} ${props.shared ? "share-confirmed" : ""}`}
        >
          {props.shared ? verb.done : verb.act}
        </Button>
      </div>
      {refused.length > 0 && (
        <div className="flex items-center gap-1 text-fg-muted-regular-small">
          <span>{plural(refused.length, "address", "es")} can't be shared</span>
          <TooltipButton
            title="Remove the addresses that can't be shared"
            aria-label="Remove the addresses that can't be shared"
            variant="contextual"
            size="xs"
            onClick={() => {
              props.onChipsChange(
                props.chips.filter((chip) => !isRefused(chip)),
              );
              props.onErrorChange(null);
            }}
          >
            <DeleteSweepOutlinedIcon />
          </TooltipButton>
        </div>
      )}
      {props.error ? (
        <span className="text-error text-sm truncate" role="alert">
          {props.error}
        </span>
      ) : (
        <span className="sr-only" role="status">
          {props.shared ? props.sharedMessage : ""}
        </span>
      )}
    </div>
  );
};

export const EmailSection: React.FC<InputShareFieldProps> = (props) => {
  const [chips, setChips] = useState<EmailChip[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isSharing, setIsSharing] = useState(false);
  // Non-personal-org tables restrict entry to the org roster (D19) — the picker
  // makes the door's membership refusal nearly unreachable.
  const shareScope = useShareScope(
    props.entityId,
    props.resourceType,
    props.rosterOrg,
  );

  const defaultPermission =
    props.resourceType === ResourceType.Model
      ? (props.defaultRole ?? UserModelRole.Collaborator)
      : (props.defaultPermission ?? Permission.CanImport);

  const [selectedPermission, setSelectedPermission] = useState<
    UserModelRole | Permission
  >(defaultPermission);

  // A dataset has no roles to pick.
  const roleOptions = useMemo(
    () =>
      props.resourceType === ResourceType.Model
        ? MODEL_ROLES.map((label) => ({
            label,
            value: UserModelRole[label as keyof typeof UserModelRole] as
              UserModelRole | Permission,
          }))
        : props.resourceType === ResourceType.Table
          ? TABLE_ACCESS_OPTIONS
          : [],
    [props.resourceType],
  );

  const handleShare = async () => {
    if (isSharing) return;
    const validEmails = chips
      .filter((chip) => chip.status === "valid")
      .map((chip) => chip.email);
    if (validEmails.length === 0) return;

    try {
      setIsSharing(true);
      const outcome = await props.shareAction(validEmails, selectedPermission);
      // What was not shared stays, red with its reason.
      setChips((now) =>
        now.filter((chip) => !validEmails.includes(chip.email)),
      );
      const atCapacity = outcome?.atCapacityEmails ?? [];
      if (atCapacity.length > 0) {
        setError(`${CAPACITY_FALLBACK} Not invited: ${atCapacity.join(", ")}`);
        setSuccessMessage(null);
        return;
      }
      // A door that serves what it can (datasets) names what it refused, so a partial
      // failure never reads as a success.
      const refused = outcome?.refusedEmails ?? [];
      if (refused.length > 0) {
        setError(`Shared, but not with: ${refused.join(", ")}`);
        setSuccessMessage(null);
        return;
      }
      setError(null);
      setSuccessMessage(`${props.resourceType} shared successfully!`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (shareError) {
      log.error("Failed to share:", shareError);
      setError(shareErrorMessage(shareError));
      setSuccessMessage(null);
    } finally {
      setIsSharing(false);
    }
  };

  const chosen = roleOptions.find((o) => o.value === selectedPermission);
  return (
    <ShareComposer
      chips={chips}
      onChipsChange={setChips}
      chipInput={{
        entityId: props.entityId,
        resourceType: props.resourceType,
        directory: shareScope.directory,
        directoryLabel: shareScope.orgName,
      }}
      roles={roleOptions.length ? roleOptions : undefined}
      role={chosen?.label}
      onRole={(label) => {
        const picked = roleOptions.find((o) => o.label === label);
        if (picked) setSelectedPermission(picked.value);
      }}
      onShare={() => void handleShare()}
      sharing={isSharing}
      shared={!!successMessage}
      disabled={props.shareDisabled}
      error={error}
      onErrorChange={setError}
      sharedMessage={successMessage ?? undefined}
      onEdit={() => setSuccessMessage(null)}
    />
  );
};
